import React, { useContext } from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { CONTENT_TYPE, MARKETS_WRAP_NICODE } from "~constants";
import Byline from "~components/shared/byline/Byline";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    hasValidMediaDataFor,
} from "~components/shared/media/";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import { PageContext } from "~context/PageContext";
import { isOpinionItem } from "~models/transforms/eyebrow";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type CuratedItem,
    CuratedItemVariation,
    isCuratedStory,
} from "~types/types";

import AudioVideoCard from "./AudioVideoCard";
import NewsletterCard from "./NewsletterCard";
import OpinionCard from "./OpinionCard";
import styles from "./Card.module.css";

type CardItem = Omit<
    CuratedItem,
    "summary" | "tickers" | "terminalTickers" | "videoCurationOverride"
>;

export enum CardSize {
    LARGE = "large",
    SMALL = "small",
}

export interface CardProps {
    size?: CardSize;
    item: CardItem;
    className?: string;
    noBasicTreatment?: boolean;
    storyIndex?: number;
    linkType?: string;
    relatedLinkType?: string;
    relatedIndex?: number;
    theme?: string;
}

export const CARD_MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 1,
    mobileMaxWidth: 72,
    desktopMaxWidth: 72,
    className: styles.mediaFromCard,
    useImageLink: false,
    showCaption: false,
};

function isDeclaredImageless(item: CardItem) {
    return item.variation === CuratedItemVariation.Imageless;
}

function isVideo(item: CardItem) {
    return item.type === CONTENT_TYPE.video;
}

function isAudio(item: CardItem) {
    return item.type === CONTENT_TYPE.audio;
}

function isNewsletter(item: CardItem) {
    return item.type === CONTENT_TYPE.newsletter_campaign;
}

export function isBasicWithSquareImage(item: CardItem): boolean {
    const { defaultImage } = item?.media || {};

    if (!defaultImage) return false;

    return (
        Boolean(defaultImage.origWidth) &&
        defaultImage.origWidth === defaultImage.origHeight
    );
}

interface PreviewErrorCardProps {
    itemType?: string;
    message?: string;
    className?: string;
}

/**
 * Filtering function which allows consuming components
 * to confirm a CuratedItem has valid data to render a
 * card in production, prior to mapping the items.
 *
 * This function will not filter out items
 * in preview to allow <PreviewErrorCard /> to return
 * helpful error messages to editors prior to publishing,
 */
export function itemIsValidForProductionCard({
    item,
    noBasicTreatment = false,
    isPreview = false,
}: {
    item: CuratedItem;
    noBasicTreatment?: boolean;
    isPreview?: boolean;
}): boolean {
    const { media } = item;

    /**
     * If media.optOut is true, editors have selected an imageless
     * version of the card and the item is valid.
     *
     * if isPreview is true, we do not want to filter items.
     *
     * Opinion Items are valid for cards.
     */
    if (
        (!noBasicTreatment && media?.optOut) ||
        isPreview ||
        isOpinionItem(item)
    ) {
        return true;
    }

    /**
     * At this point if the item does not have media, it is
     * not valid for displaying a card.
     *
     * Also, if the item is not a curated story, it cannot
     * have the required media.
     */
    if (!media || !isCuratedStory(item)) {
        return false;
    }

    const validVideoItem =
        isVideo(item) && hasValidMediaDataFor(MediaFormat.videoImage, media);
    const validAudioItem =
        isAudio(item) && hasValidMediaDataFor(MediaFormat.audioImage, media);
    const validNewsletterItem =
        isNewsletter(item) &&
        hasValidMediaDataFor(MediaFormat.newsletterImage, media);
    const validSquareImageItem =
        !noBasicTreatment && isBasicWithSquareImage(item);

    /**
     * return true only if we have valid data to render
     * media for one of the remaining specific content type.
     */
    return (
        validVideoItem ||
        validAudioItem ||
        validNewsletterItem ||
        validSquareImageItem
    );
}

export function PreviewErrorCard({
    itemType,
    message,
    className,
}: PreviewErrorCardProps) {
    const { isPreview = false } = useContext(PageContext);

    if (!isPreview) return null;

    const errorMessage =
        message ||
        `This card will not render because it is missing ${(
            <span>{itemType}</span>
        )} data.`;

    return (
        <div
            className={clsx([
                styles.card,
                styles.previewError,
                className && className,
            ])}
        >
            <h4>WARNING</h4>
            <div className={styles.errorMessage}>{errorMessage}</div>
        </div>
    );
}

function ImagelessCard({
    size = CardSize.SMALL,
    item,
    className,
    storyIndex,
    linkType = "Story",
    relatedLinkType,
    relatedIndex,
}: CardProps) {
    if (!isCuratedStory(item)) {
        console.error(`Cannot render CuratedLink in an Imageless Card`);
        return null;
    }

    const {
        byline,
        headline,
        textLabel,
        labelDropdown,
        label,
        franchise,
        variation,
        url,
        nicodes,
        updatedAt,
        publishedAt,
    } = item;

    const hasLabelData = Boolean(
        textLabel || labelDropdown || label || franchise
    );
    const showByline = variation === CuratedItemVariation.WithByline && byline;
    const hasMarketsWrapNicode = Boolean(
        nicodes?.includes(MARKETS_WRAP_NICODE)
    );

    return (
        <a
            href={url}
            data-component="imageless-card"
            data-link-type={linkType}
            data-link-index={storyIndex}
            data-link-related-type={relatedLinkType}
            data-link-related-index={relatedIndex}
            className={clsx(
                styles[size],
                styles.card,
                styles.imagelessCard,
                className
            )}
        >
            {hasLabelData && (
                <OptionalEyebrow
                    franchise={franchise}
                    label={label}
                    textLabel={textLabel}
                    presetLabel={labelDropdown}
                    className={styles.optionalEyebrow}
                    nicodes={nicodes}
                />
            )}
            {!hasLabelData && hasMarketsWrapNicode && (
                <OptionalEyebrow
                    nicodes={nicodes}
                    className={styles.optionalEyebrow}
                />
            )}
            {!hasMarketsWrapNicode && !hasLabelData && !showByline && (
                <OptionalEyebrow
                    presetLabel="relativeTimestamp"
                    updatedAt={updatedAt}
                    publishedAt={publishedAt}
                    className={styles.optionalEyebrow}
                />
            )}
            <Headline
                text={headline}
                isPhoenix
                size={HEADLINE_SIZE.EXTRA_SMALL}
            />
            {showByline && <Byline byline={byline} />}
            {variation === CuratedItemVariation.Personalized && (
                <div
                    className={clsx(
                        styles.personalizedTimestamp,
                        typographyStyles.articleLabel
                    )}
                >
                    {label}
                </div>
            )}
        </a>
    );
}

function SquareImageCard({
    size = CardSize.SMALL,
    item,
    className,
    storyIndex,
    linkType = "Story",
    relatedLinkType,
    relatedIndex,
}: CardProps) {
    if (!isCuratedStory(item)) {
        console.error(`Cannot render CuratedLink in an Square Image Card`);
        return null;
    }

    const {
        headline,
        url,
        textLabel,
        labelDropdown,
        franchise,
        label,
        nicodes,
    } = item;

    return (
        <a
            href={url}
            data-component="square-image-card"
            data-link-type={linkType}
            data-link-index={storyIndex}
            data-link-related-type={relatedLinkType}
            data-link-related-index={relatedIndex}
            className={clsx(
                styles[size],
                styles.card,
                styles.squareImageCard,
                className
            )}
        >
            <Media
                source={item}
                priority={[MediaFormat.defaultImage]}
                config={CARD_MEDIA_CONFIG}
            />
            <div className={styles.cardStoryBlock}>
                <OptionalEyebrow
                    franchise={franchise}
                    label={label}
                    textLabel={textLabel}
                    presetLabel={labelDropdown}
                    className={styles.optionalEyebrow}
                    nicodes={nicodes}
                />
                <Headline
                    text={headline}
                    isPhoenix
                    size={HEADLINE_SIZE.EXTRA_SMALL}
                />
            </div>
        </a>
    );
}

export default function Card(props: CardProps) {
    const { item, noBasicTreatment = false } = props;

    if (isOpinionItem(item)) {
        return <OpinionCard {...props} />;
    } else if (!noBasicTreatment && isDeclaredImageless(item)) {
        return <ImagelessCard {...props} />;
    } else if (isNewsletter(item)) {
        return <NewsletterCard {...props} />;
    } else if (isVideo(item) || isAudio(item)) {
        return <AudioVideoCard {...props} />;
    } else if (!noBasicTreatment && isBasicWithSquareImage(item)) {
        return <SquareImageCard {...props} />;
    } else if (noBasicTreatment) {
        return null;
    }
    return <ImagelessCard {...props} />;
}
